'use client';

import { useEffect, useId, useRef, type ReactNode } from 'react';

import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Empty state“ (60325:21971, mobile 60459:98857): bg/surface card, radius 18, 48 px padding
 * (24 / 16 on phones) and 24 px gaps, centred 48 px icon in text/brand, DLZ/Heading/H2 title and a
 * DLZ/Body/M text (max 640 px, 16 px apart, 8 on phones), then the actions and the extras.
 * `focusTitle` moves the focus to the title on mount – for a card that replaced the focused content
 * (the last favourite removed).
 */
export function FavoritesStateCard({
  icon,
  title,
  text,
  actions,
  focusTitle = false,
  className,
  children,
}: {
  icon: IconName;
  title: ReactNode;
  text: ReactNode;
  actions?: ReactNode;
  focusTitle?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusTitle) titleRef.current?.focus();
  }, [focusTitle]);

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col items-center gap-6 rounded-[18px] bg-[#F5F6F7] px-4 py-6 text-center md:p-12',
        className,
      )}
    >
      <Icon name={icon} size={48} className="text-[#D7191F]" />
      <div className="flex w-full max-w-[640px] flex-col gap-2 md:gap-4">
        <h2
          ref={titleRef}
          id={titleId}
          tabIndex={-1}
          className="dlz-h2 text-[#0F1012] outline-none"
        >
          {title}
        </h2>
        <p className="dlz-body-m text-[#4F545C]">{text}</p>
      </div>
      {actions ? <div className="flex w-full gap-3 md:w-auto">{actions}</div> : null}
      {children}
    </section>
  );
}

/** Buttons of the card: they share the phone width (48 px high), from `md` 52 px and hugging. */
export const STATE_CARD_BUTTON = 'h-12 min-w-0 flex-1 px-4 md:h-[52px] md:flex-none md:px-6';
